/**
 * Layout Design Tokens
 * Centralized layout patterns for pages, headers, sections, and grids
 * 
 * Usage:
 * import { LAYOUTS } from '@/design-system';
 * <div className={LAYOUTS.pageAdmin}>...</div>
 */

export const LAYOUTS = {
  // ─────────────────────────────────────────────────────────────────
  // PAGE CONTAINERS - Outer wrappers
  // ─────────────────────────────────────────────────────────────────
  
  /** Standard page container for authenticated pages with sidebar */
  pageAccount: "container max-w-full",
  
  /** Admin page container */
  pageAdmin: "container",
  
  /** Public page container with vertical padding */
  pagePublic: "container py-8 sm:py-12 lg:py-16",
  
  /** Full-width section container */
  sectionFull: "w-full",
  
  /** Container within full-width section */
  sectionContainer: "container",
  
  // ─────────────────────────────────────────────────────────────────
  // SECTION CONTAINERS - Full-width sections with internal containers
  // ─────────────────────────────────────────────────────────────────
  
  /** Hero section - extra large padding for above-fold content */
  heroSection: "container py-12 md:py-20 text-center",
  
  /** Standard public section padding */
  sectionStandard: "py-16",
  
  /** Compact section padding (inquiry boxes, CTAs) */
  sectionCompact: "container py-8",
  
  /** Publication content container - centered with max-width */
  publicationContainer: "container max-w-4xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8",

  /** Publication breadcrumb container - matches publication width */
  publicationBreadcrumb: "container max-w-4xl px-4 sm:px-6 lg:px-8 overflow-x-auto scrollbar-hide",
  
  // ─────────────────────────────────────────────────────────────────
  // PAGE HEADERS - Title + Description blocks
  // ─────────────────────────────────────────────────────────────────
  
  /** Header wrapper with responsive bottom margin */
  headerWrapper: "mb-section-mobile sm:mb-section-tablet lg:mb-section",
  
  /** Header with optional action - flex row on desktop */
  headerWithAction: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4",
  
  /** Header title/description stack */
  headerContent: "space-y-1 sm:space-y-2",
  
  /** Header title/description stack - centered */
  headerContentCentered: "space-y-1 sm:space-y-2 text-center",
  
  // ─────────────────────────────────────────────────────────────────
  // CONTENT WRAPPERS - Main content areas
  // ─────────────────────────────────────────────────────────────────
  
  /** Standard content spacing (space-y-6) */
  contentStack: "space-y-6",
  
  /** Compact content spacing (space-y-4) */
  contentStackCompact: "space-y-4",
  
  /** Responsive padding for main content */
  mainPadding: "py-4 sm:py-6 lg:py-8 px-page-mobile sm:px-page-tablet lg:px-page",
  
  // ─────────────────────────────────────────────────────────────────
  // SIDEBAR LAYOUTS - Main content + sidebar patterns
  // ─────────────────────────────────────────────────────────────────
  
  /** 2/3 + 1/3 sidebar grid layout */
  sidebarLayout: "grid grid-cols-1 lg:grid-cols-3 gap-6",
  
  /** 2/3 + 1/3 sidebar grid with larger gap on lg */
  sidebarLayoutSpaced: "grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8",
  
  /** Main content area spanning 2 columns */
  sidebarMain: "lg:col-span-2 space-y-6",
  
  /** Main content area spanning 2 columns (no spacing) */
  sidebarMainNoSpacing: "lg:col-span-2",
  
  /** Sidebar area */
  sidebarAside: "space-y-6",
  
  // ─────────────────────────────────────────────────────────────────
  // SECTION PATTERNS - Reusable section structures
  // ─────────────────────────────────────────────────────────────────
  
  /** Section with internal spacing */
  sectionWithHeader: "space-y-4",
  
  /** Section header row with justify-between */
  sectionHeader: "flex items-center justify-between gap-4",
  
  /** Section header start alignment */
  sectionHeaderStart: "flex items-center gap-2",
  
  // ─────────────────────────────────────────────────────────────────
  // CARD GRIDS - Responsive grid layouts
  // ─────────────────────────────────────────────────────────────────
  
  /** 2-column card grid (1 on mobile, 2 on lg) */
  cardGrid2: "grid grid-cols-1 lg:grid-cols-2 gap-6",
  
  /** 3-column card grid (1 on mobile, 2 on md, 3 on lg) */
  cardGrid3: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
  
  /** 4-column card grid (1 on mobile, 2 on sm, 4 on lg) */
  cardGrid4: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4",
  
  // ─────────────────────────────────────────────────────────────────
  // METRICS GRIDS - For stat cards and KPIs
  // ─────────────────────────────────────────────────────────────────
  
  /** Metrics row - responsive grid for stat cards */
  metricsRow: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4",
  
  /** Compact metrics row - 4 columns */
  metricsRow4: "grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4",
  
  // ─────────────────────────────────────────────────────────────────
  // ADMIN PAGE LAYOUT - Canonical admin page structure
  // ─────────────────────────────────────────────────────────────────
  
  /** Admin page container with tighter spacing */
  adminPageContainer: "space-y-4",
  
  /** Admin stats section wrapper */
  adminStatsSection: "",
  
  /** Admin toolbar section wrapper */
  adminToolbarSection: "",
  
  /** Admin content section wrapper */
  adminContentSection: "",
  
  /** Admin tab section wrapper */
  adminTabsSection: "mt-2",

  // ─────────────────────────────────────────────────────────────────
  // AUTH & PLANS SURFACES - Page and modal layout tokens
  // ─────────────────────────────────────────────────────────────────

  /** Auth page/modal grid: stats panel (left) + form card (right) */
  authGrid: "grid grid-cols-1 md:grid-cols-[2fr_3fr] lg:grid-cols-[9fr_11fr] gap-6 md:gap-8",

  /** Auth page outer container (centered, edge-safe) */
  authPageContainer: "w-[calc(100%-2rem)] sm:w-[calc(100%-4rem)] max-w-6xl mx-auto",

  /** Plans page content container */
  plansPageContainer: "max-w-5xl mx-auto",
} as const;

export type LayoutToken = keyof typeof LAYOUTS;
